import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  ListGroup,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { fires } from "../../firebase";

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "CAD",
});

const emptyReportData = {
  order: null,
  event: null,
  emails: [],
  tickets: [],
  resolvedOrderDocId: "",
};

const disputeReasonOptions = [
  { value: "product_not_received", label: "Product not received" },
  { value: "fraudulent", label: "Fraudulent / unauthorized" },
  { value: "unrecognized", label: "Unrecognized" },
  { value: "product_unacceptable", label: "Product unacceptable" },
  { value: "credit_not_processed", label: "Credit not processed" },
  { value: "duplicate", label: "Duplicate" },
  { value: "subscription_canceled", label: "Subscription canceled" },
  { value: "general", label: "General" },
];

const disputeReasonProfiles = {
  product_not_received: {
    title: "Product Not Received",
    position:
      "EventGo records show that the customer purchased admission/ticketed event services and that ticket fulfillment records were created for the booking. Where check-in records are present, they document service access or attendance activity connected to the disputed order.",
    focus:
      "The evidence below documents the purchase record, event details, ticket fulfillment records, check-in status, and customer email delivery/open status records.",
    recommendedFields:
      "service_documentation, receipt, customer_communication, uncategorized_file",
  },
  fraudulent: {
    title: "Fraudulent / Unauthorized",
    position:
      "EventGo records connect the transaction to a named customer, email address, booking reference, event selection, and ticket issuance. These records identify the charge as an EventGo booking tied to the customer information captured at purchase.",
    focus:
      "The evidence below documents customer identifiers, transaction identifiers, booking reference, email delivery/open status, and ticket usage records.",
    recommendedFields:
      "customer_communication, receipt, service_documentation, uncategorized_file",
  },
  unrecognized: {
    title: "Unrecognized",
    position:
      "EventGo records identify the merchant service, customer, booking reference, event name, and ticketed service purchased. These details support recognition of the charge as an EventGo ticket purchase.",
    focus:
      "The evidence below documents recognizable purchase details, event/service name, date, customer email status, and ticket records.",
    recommendedFields:
      "receipt, customer_communication, service_documentation, uncategorized_text",
  },
  product_unacceptable: {
    title: "Product Unacceptable",
    position:
      "EventGo records show the ticketed event/service purchased and the fulfillment records associated with the order. Where check-in records exist, they support that the service was made available or used as issued.",
    focus:
      "The evidence below documents what was purchased, when and where the service was scheduled, ticket fulfillment, and any attendance/check-in evidence.",
    recommendedFields:
      "service_documentation, customer_communication, uncategorized_file",
  },
  credit_not_processed: {
    title: "Credit Not Processed",
    position:
      "EventGo records show the payment amount, order status, recorded refund amount, and supporting context for whether a refund or credit is reflected in EventGo records.",
    focus:
      "The evidence below documents order status, amount refunded, transaction identifiers, and customer communication status records.",
    recommendedFields:
      "refund_refusal_explanation, receipt, customer_communication, uncategorized_file",
  },
  duplicate: {
    title: "Duplicate",
    position:
      "EventGo records identify the specific booking reference, customer, event, amount, and Stripe payment ID for this transaction. These details distinguish the disputed transaction from any separate booking.",
    focus:
      "The evidence below documents the unique booking reference, Firestore order document, Stripe payment ID, purchase timestamp, and purchased items.",
    recommendedFields:
      "duplicate_charge_documentation, receipt, uncategorized_file",
  },
  subscription_canceled: {
    title: "Subscription Canceled",
    position:
      "EventGo records indicate this transaction relates to a ticketed event/service booking rather than a recurring subscription. The evidence identifies the booking reference, event/service, purchase date, and ticket fulfillment records.",
    focus:
      "The evidence below documents the one-time event/service nature of the purchase, order status, ticket records, and customer communication status records.",
    recommendedFields:
      "cancellation_policy, receipt, customer_communication, uncategorized_file",
  },
  general: {
    title: "General",
    position:
      "EventGo records summarize the order, customer, event/service, ticket fulfillment, customer communication status, and source references for review by Stripe and the issuing bank.",
    focus:
      "The evidence below documents a concise factual summary of the booking and supporting source records.",
    recommendedFields:
      "receipt, customer_communication, service_documentation, uncategorized_file",
  },
};

function getDisputeProfile(disputeReason) {
  return disputeReasonProfiles[disputeReason] || disputeReasonProfiles.general;
}

function asDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(value) {
  const date = asDate(value);
  if (!date) return "N/A";
  return date.toLocaleString("en-US", {
    timeZone: "America/Toronto",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return moneyFormatter.format(amount);
}

function clean(value, fallback = "N/A") {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
}

function getEventId(order) {
  return order?.eventId || order?.eventID || order?.event_id || "";
}

function getOrderReference(order, orderId, resolvedOrderDocId) {
  return order?.uniqueId || order?.orderId || order?.id || resolvedOrderDocId || orderId;
}

function getStripePaymentId(order) {
  return (
    order?.paymentId ||
    order?.paymentIntentId ||
    order?.paymentIntent ||
    order?.stripePaymentIntentId ||
    order?.sessionId ||
    order?.stripeSessionId
  );
}

function getEvidenceOrderStatus() {
  return "Email delivered";
}

function getEventAddress(event, order) {
  return (
    event?.address?.fullAddress ||
    event?.address?.formatted_address ||
    event?.address?.address ||
    event?.fullAddress ||
    event?.address ||
    order?.location
  );
}

function normalizeEmail(doc) {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    timestamp: data.timestamp || data.createdAt || data.date || data.sentAt || null,
    messageId: data.messageId || data.message_id || "",
    status: data.status || "",
    type: data.type || "",
    from: data.from || data.sender || data.fromEmail || "",
    to: data.to || data.recipient || data.userEmail || data.email || "",
    subject: data.subject || data.title || "",
    body: data.body || data.text || data.html || data.message || "",
  };
}

function normalizeTicket(doc) {
  const data = doc.data ? doc.data() : doc;
  const id = doc.id || data.id || data.ticketId || "";
  return {
    id,
    ...data,
    ticketType: data.ticketType || data.priceDescription || data.ticketName || data.type || "Ticket",
    attendeeName: data.name || data.userName || [data.firstName, data.lastName].filter(Boolean).join(" "),
    attendeeEmail: data.email || data.userEmail || "",
    checkedIn: Boolean(data.checkinStatus || data.checkin || data.checkedIn),
    checkinTimestamp: data.checkinTimestamp || data.checkinTime || data.checkedInAt || null,
  };
}

function uniqueById(items) {
  const seen = new Set();
  return items.filter((item) => {
    const key = item.id || JSON.stringify(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildEvidenceReport({ order, event, emails, tickets, orderId, resolvedOrderDocId, disputeReason }) {
  if (!order) return "";

  const eventId = getEventId(order);
  const orderReference = getOrderReference(order, orderId, resolvedOrderDocId);
  const disputeProfile = getDisputeProfile(disputeReason);
  const checkedInTickets = tickets.filter((ticket) => ticket.checkedIn);
  const cartItems = Array.isArray(order.cartItems) ? order.cartItems : [];

  const ticketLines = tickets.length
    ? tickets
        .map((ticket, index) => {
          const status = ticket.checkedIn
            ? `Checked in at ${formatDate(ticket.checkinTimestamp)}`
            : "No check-in recorded";
          return `${index + 1}. ${clean(ticket.ticketType)} - ${clean(ticket.attendeeName || ticket.attendeeEmail)} - ${status}`;
        })
        .join("\n")
    : "No matching ticket records found in events/{eventId}/tickets.";

  const cartLines = cartItems.length
    ? cartItems
        .map((item, index) => {
          const qty = Number(item.quantity || 1);
          const price = item.price !== undefined ? ` at ${formatMoney(item.price)}` : "";
          return `${index + 1}. ${clean(item.priceDescription || item.title || item.name, "Item")} - quantity ${qty}${price}`;
        })
        .join("\n")
    : "No cart item detail recorded on the order.";

  const emailLines = emails.length
    ? emails
        .map((email, index) => {
          if (email.type === "mailersend-status" || email.status || email.messageId) {
            return `${index + 1}. ${formatDate(email.timestamp)} | Type: ${clean(email.type)} | Status: ${clean(email.status)} | Message ID: ${clean(email.messageId)}`;
          }
          const body = clean(email.body, "No body captured").replace(/\s+/g, " ").slice(0, 900);
          return `${index + 1}. ${formatDate(email.timestamp)} | From: ${clean(email.from)} | To: ${clean(email.to)} | Subject: ${clean(email.subject)}\n${body}`;
        })
        .join("\n\n")
    : "No customer email exchange records found under orders/{orderId}/emails.";

  return `Stripe and issuing bank evidence report

Dispute category
${disputeProfile.title}

Purpose of report
This report is prepared for Stripe and the issuing bank as supporting evidence for EventGo's response to the payment dispute. It summarizes records maintained by EventGo for the disputed order, including purchase details, customer information, event/service details, ticket fulfillment, email delivery status, and check-in evidence where available.

Merchant statement
${disputeProfile.position}

Evidence reviewed
${disputeProfile.focus}

Evidence summary
Booking reference: ${orderReference}
Customer: ${clean(order.userName)} (${clean(order.userEmail)})
Purchase: ${formatMoney(order.amount)} ${clean(order.currency, "CAD")} on ${formatDate(order.timestamp || order.createdAt)}
Event/service: ${clean(event?.title || order.title || order.eventTitle)}
Ticket records found: ${tickets.length}
Checked-in ticket records: ${checkedInTickets.length}
Email status records found: ${emails.length}

Order and transaction summary
Booking reference: ${orderReference}
Firestore order document: ${resolvedOrderDocId}
Dispute reference input: ${orderId}
Order status: ${getEvidenceOrderStatus()}
Payment status: ${clean(order.paymentStatus || order.stripeStatus || order.status)}
Amount: ${formatMoney(order.amount)}
Amount refunded: ${formatMoney(order.amountRefunded)}
Currency: ${clean(order.currency, "CAD")}
Purchase date: ${formatDate(order.timestamp || order.createdAt)}
Customer name: ${clean(order.userName)}
Customer email: ${clean(order.userEmail)}
Customer UID: ${clean(order.userUID || order.userId)}
Stripe payment ID: ${clean(getStripePaymentId(order))}

Product or service purchased
Event ID: ${clean(eventId)}
Event title: ${clean(event?.title || order.title || order.eventTitle)}
Event date/time: ${formatDate(event?.startDate || event?.date || event?.eventDate || order.dateSelected)}
Venue/address: ${clean(getEventAddress(event, order))}

Purchased items
${cartLines}

Fulfillment and service usage evidence
Ticket records read from events/${clean(eventId, "{eventId}")}/tickets: ${tickets.length}
Checked-in ticket records: ${checkedInTickets.length}
${ticketLines}

Customer communication
Email records read from orders/${clean(resolvedOrderDocId || orderId)}/emails: ${emails.length}
${emailLines}

Conclusion
Based on EventGo records, booking reference ${orderReference} corresponds to a ticketed event/service purchase by ${clean(order.userName)} using ${clean(order.userEmail)}. The records show the purchased event/service, the amount charged, the Stripe payment ID, ticket fulfillment records, customer email status records, and ${checkedInTickets.length > 0 ? "check-in evidence associated with the issued tickets." : "the available ticket fulfillment evidence associated with the issued tickets."}

Source record references
orders/${clean(resolvedOrderDocId)}
events/${clean(eventId)}
orders/${clean(resolvedOrderDocId || orderId)}/emails
events/${clean(eventId, "{eventId}")}/tickets
`;
}

export default function AdminStripeEvidenceCollection() {
  const { orderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [copied, setCopied] = useState(false);
  const [disputeReason, setDisputeReason] = useState("product_not_received");
  const [reportData, setReportData] = useState(emptyReportData);

  useEffect(() => {
    loadEvidence();
    // Reload evidence when the route reference changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const report = useMemo(
    () =>
      buildEvidenceReport({
        ...reportData,
        orderId,
        disputeReason,
      }),
    [reportData, orderId, disputeReason]
  );

  async function resolveOrder() {
    try {
      const directOrder = await fires.collection("orders").doc(orderId).get();
      if (directOrder.exists) {
        return {
          id: directOrder.id,
          data: { id: directOrder.id, ...directOrder.data() },
        };
      }
    } catch (err) {
      if (err.code === "permission-denied") {
        throw new Error(`No permission to read orders/${orderId}.`);
      }
      throw err;
    }

    try {
      const uniqueOrder = await fires.collection("orders").where("uniqueId", "==", orderId).limit(1).get();
      if (!uniqueOrder.empty) {
        const doc = uniqueOrder.docs[0];
        return {
          id: doc.id,
          data: { id: doc.id, ...doc.data() },
        };
      }
    } catch (err) {
      if (err.code === "permission-denied") {
        throw new Error(`No permission to query orders where uniqueId == ${orderId}.`);
      }
      throw err;
    }

    return null;
  }

  async function loadEmails(resolvedOrderDocId) {
    const emailSnapshot = await fires
      .collection("orders")
      .doc(resolvedOrderDocId)
      .collection("emails")
      .get();

    return emailSnapshot.docs
      .map(normalizeEmail)
      .sort((a, b) => (asDate(a.timestamp)?.getTime() || 0) - (asDate(b.timestamp)?.getTime() || 0));
  }

  function getTicketIds(order) {
    const ids = new Set();
    if (Array.isArray(order.ticketIds)) {
      order.ticketIds.forEach((id) => id && ids.add(String(id)));
    }
    if (Array.isArray(order.tickets)) {
      order.tickets.forEach((ticket) => {
        if (typeof ticket === "string") ids.add(ticket);
        if (ticket?.id) ids.add(String(ticket.id));
        if (ticket?.ticketId) ids.add(String(ticket.ticketId));
      });
    }
    return Array.from(ids);
  }

  async function loadTickets(order, resolvedOrderDocId) {
    const eventId = getEventId(order);
    if (!eventId) return [];

    const ticketsRef = fires.collection("events").doc(eventId).collection("tickets");
    const ticketIds = getTicketIds(order);
    const tickets = [];
    const orderReferences = uniqueById([
      { id: resolvedOrderDocId },
      { id: orderId },
      { id: order.uniqueId },
      { id: order.orderId },
    ])
      .map((item) => item.id)
      .filter(Boolean);

    const directTicketDocs = await Promise.all(ticketIds.map((ticketId) => ticketsRef.doc(ticketId).get()));
    directTicketDocs.forEach((doc) => {
      if (doc.exists) tickets.push(normalizeTicket(doc));
    });

    const querySnapshots = await Promise.all(
      orderReferences.map((reference) => ticketsRef.where("orderId", "==", reference).get())
    );
    querySnapshots.forEach((snapshot) => {
      snapshot.docs.forEach((doc) => tickets.push(normalizeTicket(doc)));
    });

    if (order.userEmail) {
      const emailSnapshot = await ticketsRef.where("userEmail", "==", order.userEmail).get();
      emailSnapshot.docs.forEach((doc) => {
        const ticket = normalizeTicket(doc);
        if (
          orderReferences.includes(ticket.orderId) ||
          ticketIds.includes(ticket.id) ||
          !ticket.orderId
        ) {
          tickets.push(ticket);
        }
      });
    }

    if (Array.isArray(order.tickets)) {
      order.tickets.forEach((ticket) => {
        if (ticket && typeof ticket === "object") {
          tickets.push(normalizeTicket(ticket));
        }
      });
    }

    return uniqueById(tickets);
  }

  async function loadEvidence() {
    if (!orderId) {
      setError("Missing orderId route parameter.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    setWarnings([]);

    try {
      const resolvedOrder = await resolveOrder();
      if (!resolvedOrder) {
        setReportData(emptyReportData);
        setError("Order not found in orders collection.");
        setLoading(false);
        return;
      }

      const order = resolvedOrder.data;
      const eventId = getEventId(order);
      const readWarnings = [];
      let eventDoc = null;
      let emails = [];
      let tickets = [];

      if (eventId) {
        try {
          eventDoc = await fires.collection("events").doc(eventId).get();
        } catch (err) {
          if (err.code === "permission-denied") {
            readWarnings.push(`No permission to read events/${eventId}.`);
          } else {
            throw err;
          }
        }
      }

      try {
        emails = await loadEmails(resolvedOrder.id);
      } catch (err) {
        if (err.code === "permission-denied") {
          readWarnings.push(`No permission to read orders/${resolvedOrder.id}/emails.`);
        } else {
          throw err;
        }
      }

      try {
        tickets = await loadTickets(order, resolvedOrder.id);
      } catch (err) {
        if (err.code === "permission-denied") {
          readWarnings.push(`No permission to read or query events/${eventId}/tickets.`);
        } else {
          throw err;
        }
      }

      setReportData({
        order,
        event: eventDoc?.exists ? { id: eventDoc.id, ...eventDoc.data() } : null,
        emails,
        tickets,
        resolvedOrderDocId: resolvedOrder.id,
      });
      setWarnings(readWarnings);
    } catch (err) {
      console.error("Error loading Stripe evidence:", err);
      setError("Failed to load evidence: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function copyReport() {
    await navigator.clipboard.writeText(report);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function downloadReport() {
    const blob = new Blob([report], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `stripe-evidence-${orderId}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function downloadPdfReport() {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const renderRoot = document.createElement("div");
    const pages = [];
    const pageWidthPx = 816;
    const pageHeightPx = 1056;
    const pagePaddingPx = 48;
    const contentHeightPx = pageHeightPx - pagePaddingPx * 2;
    const fontFamily =
      "'PingFang SC', 'Microsoft YaHei', 'Noto Sans CJK SC', 'Noto Sans SC', Arial, sans-serif";

    renderRoot.style.position = "fixed";
    renderRoot.style.left = "-9999px";
    renderRoot.style.top = "0";
    renderRoot.style.width = `${pageWidthPx}px`;
    document.body.appendChild(renderRoot);

    function createPage() {
      const page = document.createElement("div");
      page.style.width = `${pageWidthPx}px`;
      page.style.height = `${pageHeightPx}px`;
      page.style.padding = `${pagePaddingPx}px`;
      page.style.boxSizing = "border-box";
      page.style.backgroundColor = "#ffffff";
      page.style.color = "#111827";
      page.style.fontFamily = fontFamily;
      page.style.fontSize = "14px";
      page.style.lineHeight = "1.55";
      page.style.overflow = "hidden";

      const content = document.createElement("div");
      content.style.height = `${contentHeightPx}px`;
      content.style.overflow = "hidden";
      page.appendChild(content);
      renderRoot.appendChild(page);
      pages.push({ page, content });
      return { page, content };
    }

    function createTextBlock(text, options = {}) {
      const block = document.createElement(options.tag || "div");
      block.textContent = text || " ";
      block.style.margin = options.margin || "0 0 6px";
      block.style.whiteSpace = "pre-wrap";
      block.style.overflowWrap = "break-word";
      block.style.fontFamily = fontFamily;
      block.style.fontSize = options.fontSize || "14px";
      block.style.lineHeight = options.lineHeight || "1.55";
      block.style.fontWeight = options.fontWeight || "400";
      block.style.color = options.color || "#111827";
      return block;
    }

    function appendBlock(block) {
      let current = pages[pages.length - 1] || createPage();
      current.content.appendChild(block);

      if (current.content.scrollHeight > contentHeightPx && current.content.children.length > 1) {
        current.content.removeChild(block);
        current = createPage();
        current.content.appendChild(block);
      }
    }

    appendBlock(
      createTextBlock("Stripe and Issuing Bank Evidence Report", {
        tag: "h1",
        fontSize: "24px",
        fontWeight: "700",
        lineHeight: "1.25",
        margin: "0 0 12px",
      })
    );
    appendBlock(
      createTextBlock(`Order reference: ${orderId}`, {
        color: "#4b5563",
        margin: "0 0 24px",
      })
    );

    report.split("\n").forEach((line) => {
      const trimmed = line.trim();
      const isHeading = trimmed && !trimmed.includes(":") && !trimmed.startsWith("-") && trimmed.length < 70;
      appendBlock(
        createTextBlock(line, {
          fontWeight: isHeading ? "700" : "400",
          fontSize: isHeading ? "15px" : "14px",
          margin: trimmed ? (isHeading ? "12px 0 8px" : "0 0 6px") : "0 0 10px",
        })
      );
    });

    doc.setProperties({
      title: `Stripe Evidence Report - ${orderId}`,
      subject: "Stripe dispute counter evidence",
      author: "EventGo Admin",
    });

    for (let index = 0; index < pages.length; index += 1) {
      const canvas = await html2canvas(pages[index].page, {
        scale: 1.35,
        backgroundColor: "#ffffff",
        useCORS: true,
      });
      if (index > 0) doc.addPage();
      doc.addImage(
        canvas.toDataURL("image/jpeg", 0.72),
        "JPEG",
        0,
        0,
        doc.internal.pageSize.getWidth(),
        doc.internal.pageSize.getHeight(),
        undefined,
        "FAST"
      );
    }

    document.body.removeChild(renderRoot);

    doc.save(`stripe-evidence-${orderId}.pdf`);
  }

  const { order, event, emails, tickets, resolvedOrderDocId } = reportData;
  const checkedInCount = tickets.filter((ticket) => ticket.checkedIn).length;

  return (
    <>
      <Helmet>
        <title>Stripe Evidence Collection</title>
      </Helmet>
      <Container fluid className="py-4" style={{ backgroundColor: "#f7f7f7", minHeight: "100vh" }}>
        <div className="mx-auto" style={{ maxWidth: "1180px" }}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
            <div>
              <h1 className="h3 fw-bold mb-1">Stripe Evidence Collection</h1>
              <div className="text-muted">Order reference: {orderId}</div>
            </div>
            <div className="d-flex flex-wrap gap-2 align-items-end justify-content-end">
              <Form.Group style={{ minWidth: "260px" }}>
                <Form.Label className="small text-muted mb-1">Stripe dispute type</Form.Label>
                <Form.Select
                  value={disputeReason}
                  onChange={(event) => setDisputeReason(event.target.value)}
                  disabled={loading}
                >
                  {disputeReasonOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <div className="d-flex gap-2 mb-0">
                <Button variant="outline-dark" onClick={copyReport} disabled={!report || loading}>
                  {copied ? "Copied" : "Copy Report"}
                </Button>
                <Button variant="dark" onClick={downloadReport} disabled={!report || loading}>
                  Download TXT
                </Button>
                <Button variant="dark" onClick={downloadPdfReport} disabled={!report || loading}>
                  Download PDF
                </Button>
              </div>
            </div>
          </div>

          {loading && (
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center py-5">
                <Spinner animation="border" />
                <div className="mt-3">Reading Firestore evidence...</div>
              </Card.Body>
            </Card>
          )}

          {!loading && error && <Alert variant="danger">{error}</Alert>}
          {!loading && warnings.length > 0 && (
            <Alert variant="warning">
              <div className="fw-bold mb-2">Some Firestore reads were blocked by security rules.</div>
              {warnings.map((warning) => (
                <div key={warning}>{warning}</div>
              ))}
            </Alert>
          )}

          {!loading && order && (
            <>
              <Row className="g-3 mb-3">
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body>
                      <div className="text-muted small">Order Status</div>
                      <Badge bg="success">{getEvidenceOrderStatus()}</Badge>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body>
                      <div className="text-muted small">Amount</div>
                      <div className="h5 mb-0">{formatMoney(order.amount)}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body>
                      <div className="text-muted small">Emails</div>
                      <div className="h5 mb-0">{emails.length}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body>
                      <div className="text-muted small">Checked-In Tickets</div>
                      <div className="h5 mb-0">
                        {checkedInCount}/{tickets.length}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="g-3">
                <Col lg={5}>
                  <Card className="border-0 shadow-sm mb-3">
                    <Card.Header className="bg-white fw-bold">Source Records</Card.Header>
                    <ListGroup variant="flush">
                      <ListGroup.Item>
                        <div className="text-muted small">Order document</div>
                        <code>orders/{resolvedOrderDocId}</code>
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <div className="text-muted small">Event document</div>
                        <code>events/{clean(getEventId(order))}</code>
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <div className="text-muted small">Emails collection</div>
                        <code>orders/{resolvedOrderDocId}/emails</code>
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <div className="text-muted small">Tickets collection</div>
                        <code>events/{clean(getEventId(order))}/tickets</code>
                      </ListGroup.Item>
                    </ListGroup>
                  </Card>

                  <Card className="border-0 shadow-sm mb-3">
                    <Card.Header className="bg-white fw-bold">Order</Card.Header>
                    <ListGroup variant="flush">
                      <ListGroup.Item>
                        <div className="text-muted small">Customer</div>
                        <div>{clean(order.userName)} · {clean(order.userEmail)}</div>
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <div className="text-muted small">Purchase Date</div>
                        <div>{formatDate(order.timestamp || order.createdAt)}</div>
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <div className="text-muted small">Stripe Reference</div>
                        <div>{clean(getStripePaymentId(order))}</div>
                      </ListGroup.Item>
                    </ListGroup>
                  </Card>

                  <Card className="border-0 shadow-sm mb-3">
                    <Card.Header className="bg-white fw-bold">Event</Card.Header>
                    <ListGroup variant="flush">
                      <ListGroup.Item>
                        <div className="text-muted small">Title</div>
                        <div>{clean(event?.title || order.title || order.eventTitle)}</div>
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <div className="text-muted small">Date</div>
                        <div>{formatDate(event?.startDate || event?.date || event?.eventDate || order.dateSelected)}</div>
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <div className="text-muted small">Location</div>
                        <div>{clean(getEventAddress(event, order))}</div>
                      </ListGroup.Item>
                    </ListGroup>
                  </Card>
                </Col>

                <Col lg={7}>
                  <Card className="border-0 shadow-sm mb-3">
                    <Card.Header className="bg-white fw-bold">Stripe Counter Evidence Report</Card.Header>
                    <Card.Body>
                      <pre
                        className="mb-0"
                        style={{
                          whiteSpace: "pre-wrap",
                          fontSize: "13px",
                          lineHeight: 1.5,
                          maxHeight: "620px",
                          overflow: "auto",
                        }}
                      >
                        {report}
                      </pre>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="border-0 shadow-sm mt-3">
                <Card.Header className="bg-white fw-bold">Ticket Check-In Evidence</Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Ticket ID</th>
                        <th>Type</th>
                        <th>Attendee</th>
                        <th>Status</th>
                        <th>Check-In Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.length === 0 && (
                        <tr>
                          <td colSpan="5" className="text-muted text-center py-4">
                            No ticket records found.
                          </td>
                        </tr>
                      )}
                      {tickets.map((ticket) => (
                        <tr key={ticket.id || `${ticket.ticketType}-${ticket.attendeeEmail}`}>
                          <td><code>{clean(ticket.id)}</code></td>
                          <td>{clean(ticket.ticketType)}</td>
                          <td>{clean(ticket.attendeeName || ticket.attendeeEmail)}</td>
                          <td>
                            <Badge bg={ticket.checkedIn ? "success" : "secondary"}>
                              {ticket.checkedIn ? "Checked in" : "Not checked in"}
                            </Badge>
                          </td>
                          <td>{formatDate(ticket.checkinTimestamp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              <Card className="border-0 shadow-sm mt-3">
                <Card.Header className="bg-white fw-bold">Email Exchange Evidence</Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Message ID</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Subject</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emails.length === 0 && (
                        <tr>
                          <td colSpan="7" className="text-muted text-center py-4">
                            No email records found.
                          </td>
                        </tr>
                      )}
                      {emails.map((email) => (
                        <tr key={email.id}>
                          <td>{formatDate(email.timestamp)}</td>
                          <td>{clean(email.type)}</td>
                          <td>{clean(email.status)}</td>
                          <td><code>{clean(email.messageId)}</code></td>
                          <td>{clean(email.from)}</td>
                          <td>{clean(email.to)}</td>
                          <td>{clean(email.subject)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </>
          )}
        </div>
      </Container>
    </>
  );
}
